import React, { useState } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { PageHeader } from '../../components/PageHeader';
import { SearchInput } from '../../components/SearchInput';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Card } from '../../components/Card';
import { ExtractionDistributionCard } from './ExtractionDistributionCard';
import { SourceTypeDistributionCard } from './SourceTypeDistributionCard';
import { TimeseriesCard } from './TimeseriesCard';
import { CompetitorComparisonCard } from './CompetitorComparisonCard';
import { YearOverYearCard } from './YearOverYearCard';
import { CountryDistributionCard } from './CountryDistributionCard';
import { OpponentCountryCard } from './OpponentCountryCard';
import { useMediaReporting } from 'apiTyped/hooks/v2.media.query';

export const MediaReporting: React.FC = () => {
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [opponentSearch, setOpponentSearch] = useState('');

  // Fetch data from API when client is selected
  const { isLoading } = useMediaReporting({
    client: selectedClient,
    opponents: []
  });

  // Placeholder client selection
  const handleSelectClient = () => {
    if (clientSearch.trim()) {
      setSelectedClient(clientSearch.trim());
      setClientSearch('');
    }
  };

  return (
    <Page>
      <PageHeader
        title="Media Reporting"
        subtitle="Press analytics and competitor comparison"
      >
        {selectedClient && (
          <Button onClick={() => {}}>Télécharger PowerPoint</Button>
        )}
      </PageHeader>

      {/* Filters */}
      <FiltersSection>
        <FilterGroup>
          <FilterLabel>Client *</FilterLabel>
          <SearchInput
            value={clientSearch}
            onChange={setClientSearch}
            placeholder="Rechercher un client..."
          />
          {clientSearch && (
            <SelectHint onClick={handleSelectClient}>
              Press enter or click to select &ldquo;{clientSearch}&rdquo;
            </SelectHint>
          )}
          {selectedClient && (
            <SelectedChip>
              {selectedClient}
              <ChipRemove onClick={() => setSelectedClient(null)}>×</ChipRemove>
            </SelectedChip>
          )}
        </FilterGroup>

        {selectedClient && (
          <FilterGroup>
            <FilterLabel>Opponents (for comparison)</FilterLabel>
            <SearchInput
              value={opponentSearch}
              onChange={setOpponentSearch}
              placeholder="Rechercher pour ajouter des concurrents..."
            />
          </FilterGroup>
        )}
      </FiltersSection>

      {/* Charts */}
      {selectedClient ? (
        <ChartsGrid>
          <ExtractionDistributionCard />
          <SourceTypeDistributionCard />
          <TimeseriesCard />
          <CompetitorComparisonCard />
          <YearOverYearCard />
          <CountryDistributionCard />
          <OpponentCountryCard />
        </ChartsGrid>
      ) : (
        <Card padding="60px 20px">
          <EmptyState message="📊 Choisissez un client pour afficher le reporting." />
        </Card>
      )}
    </Page>
  );
};

// --- Styled Components ---

const Page = styled.div`
  max-width: 1400px;
`;

const FiltersSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 16px 20px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  margin-bottom: 24px;

  ${media.lg} {
    flex-wrap: nowrap;
  }
`;

const FilterGroup = styled.div`
  flex: 1;
  min-width: 220px;
  position: relative;
`;

const FilterLabel = styled.label`
  display: block;
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SelectHint = styled.button`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  padding: 8px 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-muted);
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: left;
  z-index: 10;

  &:hover {
    background: var(--paper);
    color: var(--ink);
  }
`;

const SelectedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 4px 10px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  background: var(--paper-warm);
  border: 1px solid var(--line);
  border-radius: var(--radius-pill);
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: var(--ink-faint);
  cursor: pointer;
  padding: 0 2px;

  &:hover {
    color: var(--terracotta);
  }
`;

const ChartsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
